// Pure identity mapping for Pendo (GT1-208). No DOM, no window, no store —
// everything here is a function of an AdminMe plus the build environment, so
// the id shapes are trivially testable and can never drift per call site.
import type { AdminMe } from '@neon-one/auctions-types';

import type { PendoAccount, PendoIdentity, PendoVisitor } from './types';

// Pendo wants first/last separately; we only ever get one display name from
// NeonSSO. First whitespace-delimited token is the first name, the rest (which
// may itself contain spaces) is the last name.
export function splitDisplayName(displayName: string | null): {
  firstName?: string;
  lastName?: string;
} {
  const normalized = (displayName ?? '').trim().replace(/\s+/g, ' ');
  if (!normalized) return {};
  const [first, ...rest] = normalized.split(' ');
  if (rest.length === 0) return { firstName: first };
  return { firstName: first, lastName: rest.join(' ') };
}

export function buildPendoIdentity(user: AdminMe, environment: string): PendoIdentity | null {
  // Both halves of the visitor id are required. Never mint a sub-based visitor
  // id as a fallback: the same human would then own two Pendo visitors that can
  // never be merged once the email-based one appears.
  if (!user.salesforceId || !user.email) return null;

  // Non-prod ids are namespaced so a dev/UAT session can never overwrite a prod
  // Pendo record, and the `environment` metadata makes filtering one-click.
  const prefix = environment === 'prod' ? '' : `${environment}-`;

  // Conditional spreads, not `key: undefined` — Pendo treats a present-but-
  // undefined field as a write, so an omitted field is the only way to leave an
  // existing record's value alone. No internal-email exclusion: we track
  // everyone and filter on `environment` in Pendo.
  const { firstName, lastName } = splitDisplayName(user.displayName);
  const visitor: PendoVisitor = {
    id: `${prefix}${user.salesforceId}-${user.email.toLowerCase()}`,
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    email: user.email,
    role: user.role,
    environment,
  };
  const account: PendoAccount = {
    id: `${prefix}${user.salesforceId}`,
    ...(user.orgName ? { name: user.orgName } : {}),
    // Only when the session can actually answer. The flag rides a JWT claim, so
    // undefined means "this token predates the claim" — writing a guessed false
    // would mislabel an internal org as a customer in Pendo, and the conditional
    // spread leaves any previously-recorded value alone instead.
    ...(typeof user.isSandbox === 'boolean' ? { isSandbox: user.isSandbox } : {}),
    environment,
  };
  return { visitor, account };
}
